import { useMemo } from 'react';

import { useBuildAccoUrlQueryParams } from '@vakantiesnl/components/src/utils/accommodation';
import {
	type FilterState,
	initialFilterState,
	isAccoWithShortDuration,
} from '@vakantiesnl/services/src/stores/filtersStore';
import type { GlobalFiltersState } from '@vakantiesnl/services/src/stores/globalFiltersStore/interfaces';
import { isPackageOffer } from '@vakantiesnl/services/src/util';
import { DEFAULT_SHORT_DURATIONS } from '@vakantiesnl/services/src/util/durations';
import { type Search } from '@vakantiesnl/types';
import { type LinkProps as NextLinkProps } from 'next/link';

type UseGetProductCardLinkProps = {
	accommodation: Search.CompactAccommodationItem;
	offer?: Search.OfferItem | Search.AccommodationOnlyOffer;
	filters?: FilterState; // can default to default filterState
	globalFilters: GlobalFiltersState;
	isFlashDeal?: boolean;
	isBot?: boolean;
	needQueryParams?: boolean;
	currentPath?: string;
	needsDurationOverride?: boolean;
};

export const useGetProductCardLink = ({
	accommodation,
	offer,
	filters = initialFilterState, // can default to default filterState
	globalFilters,
	isFlashDeal,
	isBot,
	needQueryParams = true,
	currentPath,
	needsDurationOverride = false,
}: UseGetProductCardLinkProps): NextLinkProps['href'] => {
	const isDealWithOffer = isFlashDeal && offer;
	const isOfferPackageOffer = isPackageOffer(offer);

	/** If it's a flashdeal with offers we want to have the specific offer to be shown */
	const queryParamsFilters: Partial<FilterState> = isDealWithOffer
		? {
				...(isOfferPackageOffer && {
					departureAirport: [offer.outbound.departureAirport.code],
					mealplans: [offer.rooms[0].board.code],
				}),
				priceMin: filters.priceMin,
				priceMax: filters.priceMax,
				airlines: filters.airlines,
			}
		: {
				departureAirport: filters.departureAirport,
				mealplans: filters.mealplans,
				priceMin: filters.priceMin,
				priceMax: filters.priceMax,
				airlines: filters.airlines,
			};

	let durationsOverride = needsDurationOverride && offer ? offer.durationDays.toString() : undefined;

	// When acco has no deal and no manual duration is selected, set a duration override in case acco is a short stay one
	if (!needsDurationOverride && !globalFilters.durations && isAccoWithShortDuration(filters)) {
		durationsOverride = DEFAULT_SHORT_DURATIONS;
	}

	/** Build query params for accommodation card.  */
	const queryParams = useBuildAccoUrlQueryParams(offer, queryParamsFilters, globalFilters, durationsOverride);

	/** Add refer url from the current path when the url needs query params to add the ability to go back when opening an acco in a new tab */
	if (needQueryParams && currentPath) {
		queryParams.set('referURL', currentPath);
	}

	return useMemo(() => {
		if ((!accommodation.isBookable && !accommodation.isBookableQenner) || isBot || !needQueryParams) {
			return accommodation.url;
		}
		return {
			pathname: accommodation.url,
			query: queryParams.toString(),
		};
	}, [
		accommodation.isBookable,
		accommodation.isBookableQenner,
		accommodation.url,
		isBot,
		needQueryParams,
		queryParams,
	]);
};
